import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { GAME_HAND } from 'constants/gameConstants';
import { useTableSelector } from 'hooks/useTableSelector';

import { useServerTimeout } from 'components/UserActions/ServerTimeoutBanner';

/**
 * The server's first state of a dealt hand: case "3" posts the blinds and any
 * straddle and deals, and the table reads 4 by the next broadcast. (The
 * client's GAME_HAND names this state SETUP_BIG_BLIND, which it no longer is.)
 */
const FIRST_DEALT_STATE = 4;
/** Before it (2-3) the hand the tap was for is still being set up. */
const SETTING_UP_STATES = new Set(['2', '3']);

export const STRADDLE_CANCEL_TOO_LATE = 'Too late to cancel: your straddle was posted.';
export const STRADDLE_NOT_POSTED = 'Your straddle was not posted for this hand.';

/** The player's last "straddle next hand" change that was sent, for hand `gameNo`. */
export type StraddleTap = { gameNo: number; take: boolean };

type StraddleEvidence = {
  seatId: number;
  hand: string;
  /** The table's `hasStraddle`, "1|<seat>" while a straddle is live. */
  hasStraddle: string;
  /** The reducer's pre-flop latch of the straddle seat (-1 when none). */
  latchedSeat: number;
  seatAction: string;
  move: number;
};

/**
 * Whether this seat's straddle was posted in the hand being played, or null
 * while the table cannot tell. Only positive evidence counts, because the
 * engine clears `hasStraddle` once the straddler's turn times out (checkStraddler),
 * so its absence alone proves nothing:
 * - posted: the straddle latched on, flagged for, or still read as this seat's action;
 * - not posted: another seat holds the straddle, or this seat has the first turn
 *   before the flop, which UTG only gets when no straddle went in.
 */
export const straddleOutcome = ({
  seatId,
  hand,
  hasStraddle,
  latchedSeat,
  seatAction,
  move
}: StraddleEvidence): boolean | null => {
  const [flag, flagSeat] = hasStraddle.split('|');
  const isFlagged = flag === '1';
  if (
    latchedSeat === seatId ||
    (isFlagged && Number(flagSeat) === seatId) ||
    seatAction === 'straddle'
  ) {
    return true;
  }
  if (isFlagged) return false;
  const isPreFlop = Number(hand) < GAME_HAND.DEAL_FLOP;
  return isPreFlop && move === seatId ? false : null;
};

const StraddleTapContext = createContext<(tap: StraddleTap) => void>(() => {});

/**
 * Tell the watch about the player's change, sent or held behind one still on its
 * way. A no-op without a StraddleTapWatch above.
 */
export const useRecordStraddleTap = () => useContext(StraddleTapContext);

/**
 * EN-11023 (QA, PR #4249): a straddle tap the server refuses as too late must
 * not fail in silence.
 *
 * The route has no reply channel, so a change that lands after the deal is only
 * logged (`too_late`), and the chip goes away with the offer: a late cancel
 * still posts 2x BB and nothing said so. So this compares the player's last
 * change with what the dealt hand shows (straddleOutcome) and tells them
 * when the two differ: a cancel that was posted anyway, or a take that was not
 * posted (too late, or the rules no longer held at the deal). It reads the
 * outcome rather than guessing from timing, so a change that landed just before
 * the deal says nothing.
 *
 * It waits through that hand until the table shows the outcome, so a frame first
 * seen after a reconnect, even past the flop, still counts. A hand that ends
 * without showing it, a later hand, or a player no longer seated drops the
 * change without a word.
 *
 * Mounted beside the action slot inside the table's ServerTimeoutProvider, not
 * in the chip, because the chip unmounts with its offer at the deal: the
 * moment this has to look.
 */
const StraddleTapWatch = ({ children }: { children: ReactNode }) => {
  const [lastTap, setLastTap] = useState<StraddleTap | null>(null);
  const gameNo = useTableSelector((table) => Number(table.game.gameNo) || 0);
  const hand = useTableSelector((table) => String(table.game.hand ?? ''));
  const hasStraddle = useTableSelector((table) => String(table.game.hasStraddle ?? '0|0'));
  const move = useTableSelector((table) => Number(table.game.move) || 0);
  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const seatId = useTableSelector((table) => Number(table.currentPlayer.seatId));
  const seatAction = useTableSelector((table) => String(table.game[`p${seatId}action`] ?? ''));
  const latchedSeat = useTableSelector((table) => Number(table.gameState.straddleIndicatorSeat));
  const { showNotice } = useServerTimeout();

  useEffect(() => {
    if (!lastTap) return;
    if (!isSeated || gameNo > lastTap.gameNo) {
      setLastTap(null);
      return;
    }
    if (gameNo < lastTap.gameNo) return;

    const handNo = Number(hand);
    const isBeingPlayed =
      hand !== '' && handNo >= FIRST_DEALT_STATE && handNo <= GAME_HAND.PAY_WINNERS;
    if (!isBeingPlayed) {
      if (!SETTING_UP_STATES.has(hand)) setLastTap(null);
      return;
    }

    const posted = straddleOutcome({ seatId, hand, hasStraddle, latchedSeat, seatAction, move });
    if (posted === null) return;

    setLastTap(null);
    if (!lastTap.take && posted) showNotice(STRADDLE_CANCEL_TOO_LATE);
    else if (lastTap.take && !posted) showNotice(STRADDLE_NOT_POSTED);
  }, [
    lastTap,
    gameNo,
    hand,
    hasStraddle,
    move,
    isSeated,
    seatId,
    seatAction,
    latchedSeat,
    showNotice
  ]);

  return <StraddleTapContext.Provider value={setLastTap}>{children}</StraddleTapContext.Provider>;
};

export default StraddleTapWatch;
